/**
 * Commodity Management Page (Admin)
 * Manage commodity categories, commodities, and metadata (FR-600)
 */

import React, { useState } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  Typography,
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tabs,
  Tab,
  Tooltip,
  InputAdornment,
  Divider,
  Alert,
  Snackbar
} from '@mui/material';
import { Icon } from '@iconify/react';
import { PageHeader, LoadingSkeleton, EmptyState, FilterSelect, ConfirmDialog } from '../../components/common';
import { useQueryClient } from '@tanstack/react-query';
import { useCommodities, useCategories } from '../../services/queries';
import {
  createCommodity, updateCommodity, deleteCommodity,
  createCategory, updateCategory, deleteCategory,
} from '../../services/admin';
import { useThemeMode, MONO, radius } from '../../context/ThemeContext';
import { useRecipes } from '../../theme/recipes';

// Shared monospace font for numeric values
const firaCode = { fontFamily: MONO };

const BREADCRUMBS = [
  { label: 'Home', path: '/' },
  { label: 'Admin', path: '/admin/users' },
  { label: 'Commodity Management' }
];

function CommodityManagement() {
  const { palette } = useThemeMode();
  const recipes = useRecipes();
  const queryClient = useQueryClient();
  const { data: commoditiesData = [], isLoading: l1 } = useCommodities();
  const { data: commodityCategories = [], isLoading: l2 } = useCategories();
  const loading = l1 || l2;
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState(null); // { severity, msg }
  const [activeTab, setActiveTab] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState('commodity');
  const [editItem, setEditItem] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null); // { item, type }
  const [blockedCategory, setBlockedCategory] = useState(null); // { cat, names }

  const ctaSx = { ...recipes.primaryBtnSx, px: 2.5, py: 0.9, fontSize: '0.875rem', textTransform: 'none' };

  // Controlled form state for commodity
  const [formData, setFormData] = useState({
    name: '',
    canonicalName: '',
    category: '',
    bagSizePrices: [{ bagSize: '', price: '' }],
  });

  // Controlled form state for category
  const [categoryFormData, setCategoryFormData] = useState({
    name: '',
    code: '',
    status: 'active',
  });

  const filteredCommodities = commoditiesData.filter(c =>
    c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.canonicalName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredCategories = commodityCategories.filter(c =>
    c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.code.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleAdd = (type) => {
    setDialogType(type);
    setEditItem(null);
    if (type === 'commodity') {
      setFormData({
        name: '',
        canonicalName: '',
        category: '',
        bagSizePrices: [{ bagSize: '', price: '' }],
      });
    } else {
      setCategoryFormData({ name: '', code: '', status: 'active' });
    }
    setDialogOpen(true);
  };

  const handleEdit = (item, type) => {
    setDialogType(type);
    setEditItem(item);
    if (type === 'commodity') {
      // Convert existing data to bag size/price pairs
      const pairs = item.bagSizes.map((size) => ({
        bagSize: String(size),
        price: String(
          Math.round((item.priceRange.min + item.priceRange.max) / 2)
        ),
      }));
      setFormData({
        name: item.name,
        canonicalName: item.canonicalName,
        category: item.category,
        bagSizePrices: pairs.length > 0 ? pairs : [{ bagSize: '', price: '' }],
      });
    } else {
      setCategoryFormData({
        name: item.name,
        code: item.code,
        status: item.status,
      });
    }
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setEditItem(null);
  };

  const handleSave = async () => {
    setSaving(true);
    setNotice(null);
    const savedName = dialogType === 'commodity' ? formData.name : categoryFormData.name;
    const savedAction = editItem ? 'updated' : 'created';
    try {
      if (dialogType === 'commodity') {
        const pairs = formData.bagSizePrices.filter((p) => p.bagSize && p.price);
        const bagSizes = pairs.map((p) => Number(p.bagSize));
        const prices = pairs.map((p) => Number(p.price));
        const priceRange = prices.length
          ? { min: Math.min(...prices), max: Math.max(...prices) }
          : { min: null, max: null };
        const payload = {
          name: formData.name,
          canonicalName: formData.canonicalName,
          category: formData.category,
          bagSizes,
          priceRange,
        };
        if (editItem) await updateCommodity(editItem.id, payload);
        else await createCommodity(payload);
        queryClient.invalidateQueries({ queryKey: ['commodities'] });
        queryClient.invalidateQueries({ queryKey: ['categories'] });
      } else {
        const payload = { name: categoryFormData.name, code: categoryFormData.code, status: categoryFormData.status };
        if (editItem) await updateCategory(editItem.id, payload);
        else await createCategory(payload);
        queryClient.invalidateQueries({ queryKey: ['categories'] });
      }
      setDialogOpen(false);
      setEditItem(null);
      setNotice({ severity: 'success', msg: `"${savedName}" ${savedAction}.` });
    } catch (e) {
      setNotice({ severity: 'error', msg: e?.message || 'Save failed.' });
    } finally {
      setSaving(false);
    }
  };

  // Deleting a category the platform still uses is refused by the backend,
  // so check up front and show WHICH commodities block it instead of letting
  // the confirm silently fail.
  const requestCategoryDelete = (category) => {
    const attached = commoditiesData.filter((c) => c.category === category.id);
    if (attached.length > 0) {
      setBlockedCategory({ cat: category, names: attached.map((c) => c.name) });
    } else {
      setConfirmDelete({ item: category, type: 'category' });
    }
  };

  const runDelete = async ({ item, type }) => {
    setNotice(null);
    try {
      if (type === 'commodity') {
        await deleteCommodity(item.id);
        queryClient.invalidateQueries({ queryKey: ['commodities'] });
        queryClient.invalidateQueries({ queryKey: ['categories'] });
      } else {
        await deleteCategory(item.id);
        queryClient.invalidateQueries({ queryKey: ['categories'] });
      }
      setNotice({ severity: 'success', msg: `"${item.name}" deleted.` });
    } catch (e) {
      setNotice({ severity: 'error', msg: e?.message || 'Delete failed.' });
    }
  };

  // Bag size / price pair handlers
  const addBagSizePricePair = () => {
    setFormData((prev) => ({
      ...prev,
      bagSizePrices: [...prev.bagSizePrices, { bagSize: '', price: '' }],
    }));
  };

  const removeBagSizePricePair = (index) => {
    setFormData((prev) => ({
      ...prev,
      bagSizePrices: prev.bagSizePrices.filter((_, i) => i !== index),
    }));
  };

  const updateBagSizePricePair = (index, field, value) => {
    setFormData((prev) => ({
      ...prev,
      bagSizePrices: prev.bagSizePrices.map((pair, i) =>
        i === index ? { ...pair, [field]: value } : pair
      ),
    }));
  };

  const getCategoryName = (categoryId) => {
    const cat = commodityCategories.find(c => c.id === categoryId);
    return cat?.name || 'Unknown';
  };

  if (loading) {
    return (
      <Box>
        <PageHeader
          eyebrow="Admin"
          title="Commodity Management"
          subtitle="Manage commodity categories and commodity definitions"
          breadcrumbs={BREADCRUMBS}
        />
        <LoadingSkeleton variant="page" />
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        eyebrow="Admin"
        title="Commodity Management"
        subtitle="Manage commodity categories and commodity definitions"
        breadcrumbs={BREADCRUMBS}
      />

      {/* Outcomes surface as a toast so they are visible wherever you are on
          the page (the old top-of-page banner was easy to miss). */}
      <Snackbar
        open={!!notice}
        autoHideDuration={notice?.severity === 'success' ? 3500 : 6000}
        onClose={() => setNotice(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        {notice ? <Alert severity={notice.severity} onClose={() => setNotice(null)}>{notice.msg}</Alert> : null}
      </Snackbar>

      {/* Search */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <TextField
            fullWidth
            size="small"
            placeholder="Search commodities or categories"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={recipes.searchSx}
            inputProps={{ 'aria-label': 'Search commodities or categories' }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Icon icon="ph:magnifying-glass" width={17} height={17} color={palette.textMuted} />
                </InputAdornment>
              )
            }}
          />
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs
        value={activeTab}
        onChange={(e, v) => setActiveTab(v)}
        sx={{ mb: 2, borderBottom: `1px solid ${palette.divider}`, '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, minHeight: 44 }, ...recipes.tabsSx }}
      >
        <Tab label={`Commodities (${filteredCommodities.length})`} icon={<Icon icon="ph:grains" width={16} height={16} />} iconPosition="start" />
        <Tab label={`Categories (${filteredCategories.length})`} icon={<Icon icon="ph:squares-four" width={16} height={16} />} iconPosition="start" />
      </Tabs>

      {/* Commodities Tab */}
      {activeTab === 0 && (
        <Card>
          <CardHeader
            title="Commodity List"
            titleTypographyProps={{ variant: 'h6' }}
            action={
              <Button
                variant="contained"
                disableElevation
                startIcon={<Icon icon="ph:plus" width={16} height={16} />}
                onClick={() => handleAdd('commodity')}
                sx={ctaSx}
              >
                Add Commodity
              </Button>
            }
          />
          <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    {['ID', 'Commodity Name', 'Canonical Name', 'Category', 'Bag Sizes & Prices', 'Actions'].map((col, i) => (
                      <TableCell key={col} align={i === 5 ? 'center' : 'left'} sx={recipes.headCellSx}>
                        {col}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredCommodities.map((commodity) => (
                    <TableRow key={commodity.id} hover sx={recipes.bodyRowSx}>
                      <TableCell>
                        <Typography variant="body2" sx={{ ...firaCode, color: palette.textMuted, fontSize: '0.78rem' }}>
                          {commodity.id}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {commodity.name}
                        </Typography>
                      </TableCell>
                      <TableCell>{commodity.canonicalName}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={getCategoryName(commodity.category)}
                          variant="outlined"
                          sx={{ borderColor: palette.cardBorder, color: palette.textSecondary }}
                        />
                      </TableCell>
                      <TableCell>
                        {commodity.bagSizes.map(size => {
                          const avgPrice = Math.round(
                            (commodity.priceRange.min + commodity.priceRange.max) / 2
                          );
                          return (
                            <Chip
                              key={size}
                              size="small"
                              label={`${size}kg - ₦${avgPrice.toLocaleString()}`}
                              sx={{ mr: 0.5, mb: 0.5, ...firaCode, bgcolor: palette.surface, color: palette.text, border: `1px solid ${palette.cardBorder}` }}
                            />
                          );
                        })}
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            aria-label={`Edit commodity ${commodity.name}`}
                            onClick={() => handleEdit(commodity, 'commodity')}
                            sx={{ color: palette.textMuted }}
                          >
                            <Icon icon="ph:pencil-simple" width={16} height={16} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            aria-label={`Delete commodity ${commodity.name}`}
                            onClick={() => setConfirmDelete({ item: commodity, type: 'commodity' })}
                            sx={{ color: palette.danger }}
                          >
                            <Icon icon="ph:trash" width={16} height={16} />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                  {filteredCommodities.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} sx={{ border: 0, p: 2.5 }}>
                        <EmptyState
                          compact
                          variant="dashed"
                          icon={<Icon icon="ph:grains" width={24} height={24} />}
                          title="No matching commodities"
                          description="No commodities match the current search."
                        />
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {/* Categories Tab */}
      {activeTab === 1 && (
        <Card>
          <CardHeader
            title="Category List"
            titleTypographyProps={{ variant: 'h6' }}
            action={
              <Button
                variant="contained"
                disableElevation
                startIcon={<Icon icon="ph:plus" width={16} height={16} />}
                onClick={() => handleAdd('category')}
                sx={ctaSx}
              >
                Add Category
              </Button>
            }
          />
          <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    {[
                      { label: 'ID', align: 'left' },
                      { label: 'Category Name', align: 'left' },
                      { label: 'Code', align: 'left' },
                      { label: 'Variant Count', align: 'center' },
                      { label: 'Status', align: 'left' },
                      { label: 'Actions', align: 'center' },
                    ].map((col) => (
                      <TableCell key={col.label} align={col.align} sx={recipes.headCellSx}>
                        {col.label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredCategories.map((category) => (
                    <TableRow key={category.id} hover sx={recipes.bodyRowSx}>
                      <TableCell>
                        <Typography variant="body2" sx={{ ...firaCode, color: palette.textMuted, fontSize: '0.78rem' }}>
                          {category.id}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {category.name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip size="small" label={category.code} variant="outlined" sx={{ ...firaCode, borderColor: palette.cardBorder, color: palette.textSecondary }} />
                      </TableCell>
                      <TableCell align="center" sx={firaCode}>{category.variantCount}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={category.status.toUpperCase()}
                          sx={{
                            height: 20, fontSize: '0.6rem', fontWeight: 700, letterSpacing: '0.05em',
                            bgcolor: category.status === 'active' ? palette.accentBg : palette.surfaceHover,
                            color: category.status === 'active' ? palette.accent : palette.textMuted
                          }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            aria-label={`Edit category ${category.name}`}
                            onClick={() => handleEdit(category, 'category')}
                            sx={{ color: palette.textMuted }}
                          >
                            <Icon icon="ph:pencil-simple" width={16} height={16} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            aria-label={`Delete category ${category.name}`}
                            onClick={() => requestCategoryDelete(category)}
                            sx={{ color: palette.danger }}
                          >
                            <Icon icon="ph:trash" width={16} height={16} />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                  {filteredCategories.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} sx={{ border: 0, p: 2.5 }}>
                        <EmptyState
                          compact
                          variant="dashed"
                          icon={<Icon icon="ph:squares-four" width={24} height={24} />}
                          title="No matching categories"
                          description="No categories match the current search."
                        />
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {/* Add/Edit Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleDialogClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { ...recipes.dialogPaperProps.sx, maxWidth: 560 } }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          {editItem ? 'Edit' : 'Add'} {dialogType === 'commodity' ? 'Commodity' : 'Category'}
        </DialogTitle>
        <DialogContent sx={{ bgcolor: palette.surface }}>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {dialogType === 'commodity' ? (
              <>
                <TextField
                  label="Commodity Name"
                  fullWidth
                  value={formData.name}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, name: e.target.value }))
                  }
                  sx={recipes.searchSx}
                  InputLabelProps={{ sx: recipes.labelSx }}
                />
                <TextField
                  label="Canonical Name"
                  fullWidth
                  value={formData.canonicalName}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      canonicalName: e.target.value,
                    }))
                  }
                  sx={recipes.searchSx}
                  InputLabelProps={{ sx: recipes.labelSx }}
                />
                <FilterSelect
                  label="Category"
                  size="medium"
                  value={formData.category}
                  onChange={(v) => setFormData((prev) => ({ ...prev, category: v }))}
                  options={commodityCategories.map(cat => ({ value: cat.id, label: cat.name }))}
                />

                <Divider sx={{ my: 1, borderColor: palette.divider }} />

                {/* Bag Size / Price Pairs */}
                <Box>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      mb: 1.5,
                    }}
                  >
                    <Typography variant="subtitle2" fontWeight={600}>
                      Bag Size & Price Combinations
                    </Typography>
                    <Button
                      size="small"
                      startIcon={<Icon icon="ph:plus" width={14} height={14} />}
                      onClick={addBagSizePricePair}
                      variant="outlined"
                      sx={{ color: palette.accent, borderColor: palette.accent, textTransform: 'none', fontWeight: 600, '&:hover': { bgcolor: palette.accentBg, borderColor: palette.accent } }}
                    >
                      Add Pair
                    </Button>
                  </Box>

                  {formData.bagSizePrices.map((pair, index) => (
                    <Box
                      key={index}
                      sx={{
                        display: 'flex',
                        gap: 1.5,
                        alignItems: 'center',
                        mb: 1.5,
                        p: 1.5,
                        borderRadius: `${radius.control}px`,
                        bgcolor: palette.card,
                        border: `1px solid ${palette.cardBorder}`,
                      }}
                    >
                      <TextField
                        label="Bag Size (KG)"
                        type="number"
                        size="small"
                        value={pair.bagSize}
                        onChange={(e) =>
                          updateBagSizePricePair(index, 'bagSize', e.target.value)
                        }
                        sx={{ ...recipes.searchSx, flex: 1 }}
                        InputLabelProps={{ sx: recipes.labelSx }}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">kg</InputAdornment>
                          ),
                          sx: firaCode,
                        }}
                      />
                      <TextField
                        label="Price (₦)"
                        type="number"
                        size="small"
                        value={pair.price}
                        onChange={(e) =>
                          updateBagSizePricePair(index, 'price', e.target.value)
                        }
                        sx={{ ...recipes.searchSx, flex: 1 }}
                        InputLabelProps={{ sx: recipes.labelSx }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">₦</InputAdornment>
                          ),
                          sx: firaCode,
                        }}
                      />
                      {formData.bagSizePrices.length > 1 && (
                        <IconButton
                          size="small"
                          aria-label={`Remove bag size row ${index + 1}`}
                          onClick={() => removeBagSizePricePair(index)}
                          sx={{ color: palette.danger }}
                        >
                          <Icon icon="ph:x" width={16} height={16} />
                        </IconButton>
                      )}
                    </Box>
                  ))}

                  <Typography
                    variant="caption"
                    sx={{ display: 'block', mt: 0.5, color: palette.textMuted }}
                  >
                    Add each bag size and its corresponding price separately.
                    Common sizes: 22, 25, 35, 40, 50, 65, 70, 75, 80, 100 KG
                  </Typography>
                </Box>
              </>
            ) : (
              <>
                <TextField
                  label="Category Name"
                  fullWidth
                  value={categoryFormData.name}
                  onChange={(e) =>
                    setCategoryFormData((prev) => ({
                      ...prev,
                      name: e.target.value,
                    }))
                  }
                  sx={recipes.searchSx}
                  InputLabelProps={{ sx: recipes.labelSx }}
                />
                <TextField
                  label="Code"
                  fullWidth
                  value={categoryFormData.code}
                  onChange={(e) =>
                    setCategoryFormData((prev) => ({
                      ...prev,
                      code: e.target.value,
                    }))
                  }
                  helperText="Short code for the category (e.g., RICE, MAIZE)"
                  sx={recipes.searchSx}
                  InputLabelProps={{ sx: recipes.labelSx }}
                />
                <Box>
                  <FilterSelect
                    label="Status"
                    size="medium"
                    value={categoryFormData.status}
                    onChange={(v) => setCategoryFormData((prev) => ({ ...prev, status: v }))}
                    options={[
                      { value: 'active', label: 'Active' },
                      { value: 'inactive', label: 'Inactive' },
                    ]}
                  />
                  <Typography variant="caption" sx={{ display: 'block', mt: 0.75, color: palette.textMuted, lineHeight: 1.5 }}>
                    Inactive retires the category from view: its commodities disappear from
                    pickers, filters, live-tracking cards and the public site. Recorded history
                    stays visible with a small RETIRED tag, and existing alerts and holdings
                    are not affected.
                  </Typography>
                </Box>
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose} sx={{ color: palette.textMuted, textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" disableElevation onClick={handleSave} disabled={saving} sx={ctaSx}>
            {saving ? 'Saving…' : editItem ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation - copy mirrors what the backend actually does */}
      <ConfirmDialog
        open={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        onConfirm={() => { runDelete(confirmDelete); setConfirmDelete(null); }}
        title={`Delete this ${confirmDelete?.type === 'category' ? 'category' : 'commodity'}?`}
        description={
          confirmDelete
            ? confirmDelete.type === 'category'
              ? `"${confirmDelete.item.name}" has no commodities attached and will be permanently removed. This cannot be undone.`
              : `"${confirmDelete.item.name}" will be permanently removed. If price records or aliases still reference it, the platform will refuse the deletion to protect history.`
            : 'This item will be permanently removed.'
        }
        confirmLabel="Delete"
        tone="danger"
        icon="ph:trash"
      />

      {/* Category in use - deletion blocked, show exactly what blocks it */}
      <ConfirmDialog
        open={!!blockedCategory}
        onClose={() => setBlockedCategory(null)}
        onConfirm={() => setBlockedCategory(null)}
        title="Category in use"
        description={
          blockedCategory
            ? `${blockedCategory.names.length} commodit${blockedCategory.names.length === 1 ? 'y uses' : 'ies use'} "${blockedCategory.cat.name}": ${blockedCategory.names.slice(0, 6).join(', ')}${blockedCategory.names.length > 6 ? ` and ${blockedCategory.names.length - 6} more` : ''}. Reassign or delete those commodities first - a category in use cannot be deleted.`
            : ''
        }
        confirmLabel="Got it"
        tone="primary"
        icon="ph:info"
        hideCancel
      />
    </Box>
  );
}

export default CommodityManagement;
